import { UploadOutlined } from "@mui/icons-material";
import { Button, Col, Form, Input, Row, Select, Upload, message } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { trafficSigns, signTypes } from "../../data/fakeSignals";
import "../../styles/signal-fix-components.css";
import { useTranslation } from 'react-i18next';

const { Option } = Select;

function CreateSignal({
  handleAddSign,
  selectedValues,
  onClose,
  isCreating,
  setSelectedSign,
}) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [selectedCoordinates, setSelectedCoordinates] = useState(null);
  const [imagePreview, setImagePreview] = useState(
    selectedValues?.image_url || null
  );
  const [fileData, setFile] = useState(null);

  const handleImageUpload = (info) => {
    try {
      setFile(info.file);
      if (info.file && info.file && info.file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setImagePreview(e.target.result);
          setSelectedSign((v) => {
            return { ...v, image_url: e.target.result };
          });
        };
        reader.onerror = () => {
          console.error("Error reading file");
          message.error("Error reading image file");
        };
        reader.readAsDataURL(info.file);
      } else if (info.file && !info.file.type.startsWith("image/")) {
        message.error("Please select a valid image file");
      }
    } catch (error) {
      console.error("Error in handleImageUpload:", error);
      message.error("Error processing image file");
    }
  };

  const fetchAddress = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`
      );
      const data = await response.json();
      if (data?.display_name) {
        form.setFieldsValue({ indirizzo: data.display_name });
      } else {
        form.setFieldsValue({ indirizzo: "" });
      }
    } catch (error) {
      message.error(t('signalFix.form.errorFetchingAddress'));
    }
  };

  useEffect(() => {
    const lat = form.getFieldValue("lat");
    const lng = form.getFieldValue("lng");
    if (lat && lng) fetchAddress(lat, lng);
  }, [form.getFieldValue("lat"), form.getFieldValue("lng")]);

  useEffect(() => {
    if (selectedValues) {
      form.setFieldsValue(selectedValues);
      setImagePreview(selectedValues.image_url || null);
    } else {
      form.resetFields();
      setImagePreview(null);
    }
  }, [selectedValues, form]);

  return useMemo(
    () => (
      <div className="create-signal-container">
        <Form
          form={form}
          layout="vertical"
          initialValues={selectedValues}
          onFinish={(values) => {
            handleAddSign(values, imagePreview, fileData);
            form.resetFields();
            setImagePreview(null);
          }}
        >
          <h3>
            <b>
              {selectedValues
                ? t('signalFix.form.modifySignal')
                : t('signalFix.form.addNewSignal')}
            </b>
          </h3>
          <br />
          <Form.Item name="id" style={{ display: "none" }}>
            <input />
          </Form.Item>
          <Form.Item label={t('signalFix.form.uploadImage')} name="image">
            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              id="hidden-upload-input"
              onChange={(e) => {
                try {
                  const file = e.target.files?.[0];
                  setFile(file);
                  if (
                    file &&
                    file instanceof Blob &&
                    file.type.startsWith("image/")
                  ) {
                    const reader = new FileReader();
                    reader.onload = (e) => setImagePreview(e.target.result);
                    reader.onerror = () => {
                      console.error("Error reading file");
                      message.error("Error reading image file");
                    };
                    reader.readAsDataURL(file);
                  } else if (file && !file.type.startsWith("image/")) {
                    message.error("Please select a valid image file");
                    setFile(null);
                  }
                } catch (error) {
                  console.error("Error in file input onChange:", error);
                  message.error("Error processing image file");
                  setFile(null);
                }
              }}
            />
            {imagePreview ? (
              <div
                className="signal-image-preview-container"
                onClick={() =>
                  document.getElementById("hidden-upload-input").click()
                }
              >
                <img
                  src={imagePreview}
                  alt="preview"
                  className="signal-image-preview"
                />
                <div className="signal-image-overlay">
                  <UploadOutlined className="signal-upload-icon" />
                  {t('signalFix.form.clickToReplace')}
                </div>
              </div>
            ) : (
              <Upload.Dragger
                accept="image/*"
                maxCount={1}
                showUploadList={false}
                beforeUpload={() => false}
                onChange={handleImageUpload}
                className="signal-upload-dropzone"
              >
                <div className="dropzone-inner">
                  <span className="dropzone-icon">
                    <UploadOutlined />
                  </span>
                  <div className="dropzone-text">
                    <b>{t('signalFix.form.uploadOrDrag')}</b>
                  </div>
                </div>
              </Upload.Dragger>
            )}
          </Form.Item>
          <Form.Item label={t('signalFix.form.category')} name="type" rules={[{ required: true }]}>
            <Select
              className="signal-form-input"
              showSearch
              optionFilterProp="label"
            >
              {Object.entries(signTypes).map(([key, { value, icon }]) => (
                <Option key={key} value={key} label={value}>
                  {icon} {value}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={t('signalFix.form.latitude')}
                name="lat"
                rules={[{ required: true }]}
              >
                <Input
                  className="signal-form-input"
                  type="number"
                  step="0.000001"
                  onChange={() => {
                    const lat = form.getFieldValue("lat");
                    const lng = form.getFieldValue("lng");
                    if (lat && lng) fetchAddress(lat, lng);
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={t('signalFix.form.longitude')}
                name="lng"
                rules={[{ required: true }]}
              >
                <Input
                  type="number"
                  step="0.000001"
                  onChange={() => {
                    const lat = form.getFieldValue("lat");
                    const lng = form.getFieldValue("lng");
                    if (lat && lng) fetchAddress(lat, lng);
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label={t('signalFix.form.address')} name="indirizzo">
            <Input readOnly />
          </Form.Item>

          <Form.Item label={t('signalFix.form.internalId')} name="id">
            <Input disabled={true} />
          </Form.Item>

          <Form.Item label={t('signalFix.form.officialId')} name="external_id">
            <Input />
          </Form.Item>

          <Form.Item>
            <Row gutter={[10, 10]} justify="space-between">
              <Col span={12}>
                <Button
                  className="signal-submit-button"
                  onClick={() => {
                    form.resetFields();
                    setImagePreview(null);
                    onClose();
                  }}
                >
                  {t('signalFix.form.cancel')}
                </Button>
              </Col>
              <Col span={12}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isCreating}
                  disabled={isCreating}
                  className="signal-submit-button"
                >
                  {isCreating ? t('signalFix.form.creating') : t('signalFix.form.create')}
                </Button>
              </Col>
            </Row>
          </Form.Item>
        </Form>
      </div>
    ),
    [selectedValues, imagePreview]
  );
}

export default CreateSignal;
